import { useRef, useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

/**
 * Admin image-upload field with CDN-aware backend.
 * - Backend tries Cloudinary first, falls back to base64-Mongo if creds are
 *   missing or the upload fails. We just take whichever URL the API returns.
 * - `bucket` prop chooses the Cloudinary subfolder (uploads/products/stories
 *   /hero/cta/articles/founders) so admins can browse media by section in
 *   the Cloudinary console later.
 * - The preview thumbnail uses an `onError` handler so a deleted CDN asset
 *   degrades to a gentle placeholder instead of a broken-image icon.
 */
export default function ImageUploadField({
  label,
  value,
  onChange,
  testid,
  bucket = "uploads",
  helperText,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setPreviewError(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/uploads?bucket=${encodeURIComponent(bucket)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      onChange(data.url);
      toast.success(
        data.kind === "cloudinary" ? "Uploaded to CDN" : "Uploaded"
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-1 mt-2">
      {label ? <Label className="text-xs">{label}</Label> : null}
      <div className="flex items-center gap-2">
        <Input
          value={value || ""}
          onChange={(e) => {
            setPreviewError(false);
            onChange(e.target.value);
          }}
          placeholder="Paste image URL or upload →"
          data-testid={testid}
          className="rounded-lg"
        />
        <input
          type="file"
          accept="image/*"
          ref={inputRef}
          onChange={onFile}
          className="hidden"
          data-testid={`${testid}-file-input`}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="rounded-lg whitespace-nowrap"
          data-testid={`${testid}-upload-btn`}
        >
          <Upload className="w-4 h-4 mr-1" /> {busy ? "Uploading…" : (value ? "Replace" : "Upload")}
        </Button>
        {value ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => { setPreviewError(false); onChange(""); }}
            className="rounded-lg text-red-600"
            data-testid={`${testid}-clear-btn`}
            aria-label="Clear image"
          >
            <X className="w-4 h-4" />
          </Button>
        ) : null}
      </div>
      {helperText ? (
        <p className="text-[11px] text-emerald-900/55 leading-relaxed">{helperText}</p>
      ) : null}
      {value ? (
        previewError ? (
          <div className="mt-2 w-32 h-24 rounded-lg border border-amber-200 bg-amber-50 flex items-center justify-center text-[10px] uppercase tracking-[0.18em] text-amber-700/80 font-semibold text-center px-2">
            Image not<br />reachable
          </div>
        ) : (
          <img
            src={value}
            alt={`${label || "image"} preview`}
            onError={() => setPreviewError(true)}
            className="mt-2 w-32 h-24 object-cover rounded-lg border border-emerald-100"
          />
        )
      ) : null}
    </div>
  );
}
